import { useState, useCallback } from "react";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import CodeBlock from "./CodeBlock";

const endpointOptions = [
  { value: "GET:/api/v1/agents", label: "GET /api/v1/agents" },
  { value: "GET:/api/v1/agents/:id", label: "GET /api/v1/agents/:id" },
  { value: "POST:/api/v1/agents", label: "POST /api/v1/agents" },
  { value: "PUT:/api/v1/agents/:id", label: "PUT /api/v1/agents/:id" },
  { value: "DELETE:/api/v1/agents/:id", label: "DELETE /api/v1/agents/:id" },
  { value: "GET:/api/v1/messages", label: "GET /api/v1/messages" },
  { value: "POST:/api/v1/messages", label: "POST /api/v1/messages" },
  { value: "GET:/api/v1/channels", label: "GET /api/v1/channels" },
  { value: "POST:/api/v1/channels", label: "POST /api/v1/channels" },
  { value: "GET:/api/v1/analytics/overview", label: "GET /api/v1/analytics/overview" },
  { value: "GET:/api/v1/analytics/messages", label: "GET /api/v1/analytics/messages" },
  { value: "GET:/api/v1/webhooks", label: "GET /api/v1/webhooks" },
  { value: "POST:/api/v1/webhooks", label: "POST /api/v1/webhooks" },
];

const mockResponses = {
  "GET:/api/v1/agents": {
    data: [
      { id: "agt_001", name: "Support Agent", status: "active", channels: ["telegram", "whatsapp"] },
      { id: "agt_002", name: "Sales Bot", status: "active", channels: ["slack"] },
    ],
    total: 2,
    limit: 20,
    offset: 0,
  },
  "GET:/api/v1/agents/:id": {
    id: "agt_001",
    name: "Support Agent",
    model: "maxos-4",
    status: "active",
    channels: ["telegram", "whatsapp"],
    config: { temperature: 0.7, max_tokens: 2048 },
    created_at: "2026-01-15T10:30:00Z",
    updated_at: "2026-05-20T08:00:00Z",
  },
  "POST:/api/v1/agents": {
    id: "agt_new_001",
    name: "New Agent",
    model: "maxos-4",
    system_prompt: "You are a helpful assistant.",
    status: "active",
    channels: [],
    created_at: new Date().toISOString(),
  },
  "PUT:/api/v1/agents/:id": {
    id: "agt_001",
    name: "Updated Agent Name",
    model: "maxos-4",
    status: "active",
    updated_at: new Date().toISOString(),
  },
  "DELETE:/api/v1/agents/:id": {
    success: true,
    message: "Agent agt_001 deleted",
  },
  "GET:/api/v1/messages": {
    data: [
      { id: "msg_789", content: "Hello! How can I help?", direction: "outbound", status: "delivered", created_at: "2026-06-04T09:15:00Z" },
      { id: "msg_788", content: "I need help with my order", direction: "inbound", status: "received", created_at: "2026-06-04T09:14:00Z" },
    ],
    total: 2,
    limit: 20,
    offset: 0,
  },
  "POST:/api/v1/messages": {
    id: "msg_new_001",
    agent_id: "agt_001",
    channel_id: "ch_telegram_001",
    content: "Hello! How can I help?",
    direction: "outbound",
    status: "sent",
    created_at: new Date().toISOString(),
  },
  "GET:/api/v1/channels": {
    data: [
      { id: "ch_telegram_001", type: "telegram", name: "Main Bot", status: "connected", connected_at: "2026-01-15T10:30:00Z" },
      { id: "ch_slack_001", type: "slack", name: "Engineering", status: "connected", connected_at: "2026-02-01T08:00:00Z" },
    ],
    total: 2,
  },
  "POST:/api/v1/channels": {
    id: "ch_telegram_002",
    type: "telegram",
    name: "Support Bot",
    status: "connected",
    connected_at: new Date().toISOString(),
  },
  "GET:/api/v1/analytics/overview": {
    period: { from: "2026-06-01", to: "2026-06-04" },
    messages: { total: 12450, inbound: 6800, outbound: 5650 },
    agents: { total: 5, active: 4 },
    channels: { total: 3, connected: 3 },
    avg_response_time_ms: 340,
    satisfaction_score: 4.7,
  },
  "GET:/api/v1/analytics/messages": {
    granularity: "daily",
    data: [
      { date: "2026-06-01", inbound: 320, outbound: 280 },
      { date: "2026-06-02", inbound: 410, outbound: 350 },
      { date: "2026-06-03", inbound: 380, outbound: 310 },
    ],
  },
  "GET:/api/v1/webhooks": {
    data: [
      { id: "wh_001", url: "https://example.com/webhook", events: ["message.received"], status: "active" },
    ],
  },
  "POST:/api/v1/webhooks": {
    id: "wh_002",
    url: "https://your-server.com/webhook",
    events: ["message.received", "agent.created"],
    secret: "whsec_****",
    status: "active",
    created_at: new Date().toISOString(),
  },
};

export default function ApiPlayground() {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState("");
  const [selectedEndpoint, setSelectedEndpoint] = useState("GET:/api/v1/agents");
  const [customPath, setCustomPath] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedOpt = endpointOptions.find((o) => o.value === selectedEndpoint);
  const [method] = selectedEndpoint.split(":");

  const handleTest = useCallback(() => {
    if (!apiKey.trim()) {
      setResponse({ error: { code: "missing_api_key", message: "Please enter an API key (any value works in mock mode)" } });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const mock = mockResponses[selectedEndpoint];
      if (mock) {
        setResponse(mock);
      } else {
        setResponse({ error: { code: "not_found", message: `Endpoint ${selectedEndpoint} not found in mock data` } });
      }
      setLoading(false);
    }, 800);
  }, [selectedEndpoint, apiKey]);

  return (
    <div className="space-y-4">
      {/* Request builder */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
            {t("api_docs.playground.api_key_label")}
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={t("api_docs.playground.api_key_placeholder")}
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-sm font-mono text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
            {t("api_docs.playground.endpoint_label")}
          </label>
          <select
            value={selectedEndpoint}
            onChange={(e) => setSelectedEndpoint(e.target.value)}
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-sm font-mono text-zinc-300 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20"
          >
            {endpointOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{t('api_playground.custom_path_params')}</label>
          <input
            type="text"
            value={customPath}
            onChange={(e) => setCustomPath(e.target.value)}
            placeholder="e.g. agt_001"
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-sm font-mono text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20"
          />
        </div>
      </div>

      {/* Method + Path preview */}
      <div className="flex items-center gap-2 p-3 rounded-md bg-zinc-900 border border-zinc-800">
        <span className={`shrink-0 px-2 py-0.5 font-mono text-[11px] font-bold uppercase rounded ${
          method === "GET" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
          method === "POST" ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" :
          method === "PUT" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" :
          "bg-red-500/20 text-red-400 border border-red-500/30"
        }`}>
          {method}
        </span>
        <code className="text-sm font-mono text-zinc-300 break-all">
          https://api.maxoscloud.com/v1{selectedOpt?.label.split(" ").slice(1).join(" ").replace(":id", customPath || ":id")}
        </code>
      </div>

      <button
        onClick={handleTest}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-mono text-xs font-bold uppercase tracking-wider rounded-md hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <span className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            {t("api_docs.playground.sending")}
          </>
        ) : (
          <>
            <Play size={14} />
            {t("api_docs.playground.try_it")}
          </>
        )}
      </button>

      {response && (
        <div>
          <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {t("api_docs.playground.response_label")}
          </h4>
          <CodeBlock code={JSON.stringify(response, null, 2)} language="json" />
        </div>
      )}
    </div>
  );
}
